import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import "./level.css";
import Table from "./components/Table";
import ExportToExcel from "../../../components/ExportToExcel";
import Header from "../../../components/Header";
import Loader from "../../../components/Loader";
import { API_BASE_URL, authFetch } from "../../../config/api";
import { dataFetch } from "../../../utils/dataFetch";

function Professionals() {
  const target = useRef();
  const { level, semester, session, class_id } = useParams();
  const [students, setStudents] = useState([]);
  const [load, setLoad] = useState(false);
  const [error, setError] = useState(null);
  const [sortMode, setSortMode] = useState("none");

  const compareByName = useCallback((a, b) => {
    const normalize = (v) =>
      String(v ?? "")
        .replace(/\s+/g, " ")
        .trim();
    const nc = normalize(a?.fullname).localeCompare(
      normalize(b?.fullname),
      "en",
      { sensitivity: "base", numeric: true },
    );
    if (nc !== 0) return nc;
    return normalize(a?.reg_no).localeCompare(normalize(b?.reg_no), "en", {
      sensitivity: "base",
      numeric: true,
    });
  }, []);

  const fetchStudents = useCallback(async () => {
    if (!class_id || !session || !semester || !level) return;
    setLoad(true);
    setError(null);
    try {
      let data;
      try {
        const params = new URLSearchParams({
          class_id,
          session,
          semester,
          level,
        });
        const response = await authFetch(
          `${API_BASE_URL}/api/class/fetch?${params}`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        if (text.trim().startsWith("<")) throw new Error("HTML response");
        const json = JSON.parse(text);
        data = json.students || [];
      } catch {
        const json = await dataFetch("getClass", {
          class_id,
          session,
          semester,
          level,
        });
        data = json.students || [];
      }
      setStudents(data);
    } catch (err) {
      console.error("Failed to fetch students:", err);
      setError(err.message);
      setStudents([]);
    } finally {
      setLoad(false);
    }
  }, [class_id, session, semester, level]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const filename = useMemo(
    () =>
      `${session}: ${level} Level: ${semester === "1" ? "first semester" : "second semester"} professional results`,
    [session, level, semester],
  );

  const sortedStudents = useMemo(() => {
    if (!students?.length) return students;
    const sorted = [...students];
    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    if (sortMode === "none") sorted.sort(compareByName);
    else if (sortMode === "semester_gpa")
      sorted.sort((a, b) => {
        const d = toNum(b.gpa) - toNum(a.gpa);
        return d !== 0 ? d : compareByName(a, b);
      });
    else if (sortMode === "session_gpa")
      sorted.sort((a, b) => {
        const d = toNum(b.session_gpa) - toNum(a.session_gpa);
        return d !== 0 ? d : compareByName(a, b);
      });
    else if (sortMode === "cgpa")
      sorted.sort((a, b) => {
        const d = toNum(b.cgpa) - toNum(a.cgpa);
        return d !== 0 ? d : compareByName(a, b);
      });
    return sorted;
  }, [students, sortMode, compareByName]);

  useEffect(() => {
    setSortMode("none");
  }, [class_id, session, semester, level]);

  if (error) {
    return (
      <>
        <Header />
        <div
          className="current_level"
          style={{ textAlign: "center", padding: "2rem" }}
        >
          <div className="error-message" style={{ color: "red" }}>
            <h3>Error loading professional results</h3>
            <p>{error}</p>
            <button
              onClick={fetchStudents}
              style={{ marginTop: "1rem", padding: "0.5rem 1rem" }}
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="no_print"
        style={{
          position: "sticky",
          top: "12px",
          zIndex: 10,
          backgroundColor: "white",
          padding: "8px 12px",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
          border: "1px solid #cbd5e1",
          margin: "1rem auto",
          maxWidth: "980px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <div style={{ fontWeight: "600", color: "#334155" }}>Actions</div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {[
            { key: "semester_gpa", label: "Sort by Semester GPA" },
            { key: "session_gpa", label: "Sort by Session GPA" },
            { key: "cgpa", label: "Sort by Overall GPA" },
            { key: "none", label: "Sort A-Z" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSortMode(key)}
              style={{
                padding: "4px 10px",
                border: "none",
                backgroundColor: sortMode === key ? "#0f172a" : "#e9ecef",
                color: sortMode === key ? "white" : "#333",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "bold",
                borderRadius: "4px",
              }}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => window.print()}
            style={{
              padding: "4px 10px",
              border: "none",
              backgroundColor: "#007bff",
              color: "white",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: "bold",
              borderRadius: "4px",
            }}
          >
            Print
          </button>
          <ExportToExcel tableId="myTable" filename={filename} />
        </div>
      </div>
      <Header />
      <div
        className="current_level"
        ref={target}
        id="pageContent"
        style={{ textAlign: "center" }}
      >
        <div className="header">
          <h2>
            {session}: {level} Level:{" "}
            {semester === "1" ? "first semester" : "second semester"}{" "}
            professional results
          </h2>
        </div>
        {load && <Loader />}
        {!load && students.length === 0 && !error && (
          <div style={{ padding: "2rem" }}>
            <p>No students found for this class.</p>
          </div>
        )}
        {students.length > 0 && <Table students={sortedStudents} />}
      </div>
      <div className="gp_tab no_print">
        <div className="transcript_btn"></div>
        <div></div>
      </div>
    </>
  );
}

export default React.memo(Professionals);
